/**
 * The goal form's rules, shared by every place a goal is created (2026-10):
 * the student's own "Yeni Hedef" and the teacher's "Hedef Ata" (one student or
 * a whole class), on web and mobile.
 * Web (dijipo-web-main/…/src/utils/goalForm.ts) and mobile (dijipo-mobile/utils/goalForm.ts)
 * keep byte-for-byte identical copies — change both.
 *
 * A goal is named by whoever creates it (no fixed "Hedef Türü"), runs for a
 * day / week / month, may hold a time range on its day ("Tüm gün" = none), and
 * says what counts toward it: LMS tests, the student's own entries, or both.
 */

export type GoalPeriod = 'daily' | 'weekly' | 'monthly';
export type GoalTracking = 'lms' | 'manual' | 'both';
export type GoalFormRole = 'student' | 'teacher';

export interface GoalFormValue {
  title: string;
  period: GoalPeriod;
  /** YYYY-MM-DD — for a week or month, any day inside it. */
  date: string;
  /** Daily goals only: no time range. */
  wholeDay: boolean;
  /** HH:mm */
  startTime: string;
  endTime: string;
  subjectId: number | null;
  subjectUnitId: number | null;
  /** As typed. */
  target: string;
  tracking: GoalTracking;
}

export const GOAL_TITLE_MAX = 80;

/**
 * A fresh form. A student's goal defaults to the whole day; a teacher's keeps
 * the time range the Hedef Ata form always had (it lands in a calendar slot).
 */
export function emptyGoalForm(role: GoalFormRole, todayIso: string, overrides: Partial<GoalFormValue> = {}): GoalFormValue {
  return {
    title: '',
    period: 'daily',
    date: todayIso,
    wholeDay: role === 'student',
    startTime: '18:00',
    endTime: '19:00',
    subjectId: null,
    subjectUnitId: null,
    target: '20',
    tracking: 'both',
    ...overrides,
  };
}

export const GOAL_PERIOD_OPTIONS: { value: GoalPeriod; label: string; hint: string }[] = [
  { value: 'daily', label: 'Günlük', hint: 'Tek gün' },
  { value: 'weekly', label: 'Haftalık', hint: 'Pazartesi–Pazar' },
  { value: 'monthly', label: 'Aylık', hint: 'Ayın tamamı' },
];

export const GOAL_DATE_LABEL: Record<GoalPeriod, string> = {
  daily: 'Hangi gün?',
  weekly: 'Hangi hafta? (o haftadan bir gün seç)',
  monthly: 'Hangi ay? (o aydan bir gün seç)',
};

const MONTH_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const DAY_LONG = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

/** The window a goal covers — mirrors the backend's computeGoalPeriod (UTC math). */
export function resolveGoalWindow(period: GoalPeriod, dateIso: string): { start: Date; end: Date } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) return null;
  const ref = new Date(`${dateIso}T00:00:00Z`);
  if (isNaN(ref.getTime())) return null;
  const y = ref.getUTCFullYear(), m = ref.getUTCMonth(), d = ref.getUTCDate();
  if (period === 'weekly') {
    const diff = (ref.getUTCDay() + 6) % 7; // days since Monday
    return { start: new Date(Date.UTC(y, m, d - diff)), end: new Date(Date.UTC(y, m, d - diff + 6)) };
  }
  if (period === 'monthly') return { start: new Date(Date.UTC(y, m, 1)), end: new Date(Date.UTC(y, m + 1, 0)) };
  return { start: ref, end: ref };
}

const shortDay = (d: Date) => `${d.getUTCDate()} ${MONTH_SHORT[d.getUTCMonth()]}`;

/** "2 Eki, Cuma" for a day; "29 Eyl – 5 Eki" for a week; "1 Eki – 31 Eki" for a month. */
export function formatGoalWindow(period: GoalPeriod, dateIso: string): string | null {
  const w = resolveGoalWindow(period, dateIso);
  if (!w) return null;
  if (period === 'daily') return `${shortDay(w.start)}, ${DAY_LONG[w.start.getUTCDay()]}`;
  return `${shortDay(w.start)} – ${shortDay(w.end)}`;
}

export interface GoalTrackingOption {
  value: GoalTracking;
  label: string;
  detail: string;
  /** Counted by the LMS — the ⚡ mark. */
  auto: boolean;
  /** The student adds solved questions by hand — the ✎ mark. */
  manual: boolean;
}

export function goalTrackingOptions(role: GoalFormRole): GoalTrackingOption[] {
  const t = role === 'teacher';
  return [
    {
      value: 'lms', label: 'LMS testleri', auto: true, manual: false,
      detail: t ? "Öğrencinin eis ekosis LMS'te çözdüğü sorular kendiliğinden sayılır." : "eis ekosis LMS'te çözdüğün sorular kendiliğinden sayılır.",
    },
    {
      value: 'manual', label: 'Elle eklenen', auto: false, manual: true,
      detail: t ? 'Öğrenci çözdüklerini kendisi ekler.' : 'Çözdüklerini kendin eklersin.',
    },
    {
      value: 'both', label: 'İkisi de', auto: true, manual: true,
      detail: t ? "LMS'tekiler kendiliğinden sayılır; öğrenci başka yerde çözdüklerini de ekler." : "LMS'tekiler kendiliğinden sayılır; başka yerde çözdüklerini de eklersin.",
    },
  ];
}

/** Under the options: what the ⚡ mark means wherever the goal shows up later. */
export function goalAutoNote(role: GoalFormRole): string {
  return role === 'teacher'
    ? "işaretli hedefler, öğrencinin LMS'te çözdüğü sorularla otomatik sayılır."
    : "işaretli hedefler, LMS'te çözdüğün sorularla otomatik sayılır.";
}

/** Shown under the konu picker when the LMS counts toward the goal. */
export const GOAL_KONU_HINT = "LMS'te yalnızca bu konuya bağlı sorular sayılır.";

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

/** The first thing to fix, or null when the form can be sent. */
export function validateGoalForm(v: GoalFormValue): string | null {
  const title = v.title.trim();
  if (!title) return 'Hedefe bir ad verin.';
  if (title.length > GOAL_TITLE_MAX) return `Hedef adı en fazla ${GOAL_TITLE_MAX} karakter olabilir.`;
  if (!resolveGoalWindow(v.period, v.date)) return 'Geçerli bir tarih seçin.';
  if (v.period === 'daily' && !v.wholeDay) {
    if (!HHMM.test(v.startTime) || !HHMM.test(v.endTime)) return 'Başlangıç ve bitiş saatini seçin.';
    if (toMin(v.endTime) <= toMin(v.startTime)) return 'Bitiş saati başlangıçtan sonra olmalı.';
  }
  const n = Number(v.target);
  if (!Number.isInteger(n) || n <= 0) return 'Geçerli bir soru sayısı girin.';
  if (n > 5000) return 'Soru sayısı en fazla 5000 olabilir.';
  return null;
}

/** A time range is sent only for a daily goal that is not "Tüm gün". */
export const goalHasTimeRange = (v: GoalFormValue): boolean => v.period === 'daily' && !v.wholeDay;

/** Body for POST /student/goals. */
export function studentGoalPayload(v: GoalFormValue) {
  const timed = goalHasTimeRange(v);
  return {
    title: v.title.trim(),
    periodType: v.period,
    date: v.date,
    startTime: timed ? v.startTime : undefined,
    endTime: timed ? v.endTime : undefined,
    subjectId: v.subjectId ?? undefined,
    subjectUnitId: v.subjectUnitId ?? undefined,
    targetCount: Number(v.target),
    trackingMode: v.tracking,
  };
}

/** Body for POST /teacher/goals/assign-student and /assign-class/:id. */
export function teacherGoalPayload(v: GoalFormValue) {
  const timed = goalHasTimeRange(v);
  return {
    title: v.title.trim(),
    periodType: v.period,
    goalDate: v.date,
    startTime: timed ? v.startTime : undefined,
    endTime: timed ? v.endTime : undefined,
    subjectId: v.subjectId ?? undefined,
    subjectUnitId: v.subjectUnitId ?? undefined,
    targetValue: Number(v.target),
    trackingMode: v.tracking,
  };
}
